import Dexie, { type EntityTable } from 'dexie'
import type {
  Ajustes,
  Asistencia,
  Bitacora,
  Cliente,
  Devolucion,
  Gasto,
  Lote,
  Movimiento,
  Producto,
  Usuario,
  Venta,
} from '../types'
import {
  AJUSTES_POR_DEFECTO,
  type Adapter,
  type ResultadoStock,
  type Snapshot,
  type Tabla,
} from './adapter'

class NovaBerryDB extends Dexie {
  usuarios!: EntityTable<Usuario, 'id'>
  productos!: EntityTable<Producto, 'id'>
  lotes!: EntityTable<Lote, 'id'>
  movimientos!: EntityTable<Movimiento, 'id'>
  clientes!: EntityTable<Cliente, 'id'>
  ventas!: EntityTable<Venta, 'id'>
  devoluciones!: EntityTable<Devolucion, 'id'>
  bitacora!: EntityTable<Bitacora, 'id'>
  gastos!: EntityTable<Gasto, 'id'>
  asistencia!: EntityTable<Asistencia, 'id'>
  ajustes!: EntityTable<Ajustes, 'id'>

  constructor() {
    super('novaberry')
    this.version(1).stores({
      usuarios: 'id, email, rol',
      productos: 'id, codigo, categoria, nombre, codigoBarras',
      lotes: 'id, productoId, vencimiento',
      movimientos: 'id, productoId, fecha, tipo',
      clientes: 'id, tipo, nombre, rut',
      ventas: 'id, folio, clienteId, usuarioId, fecha, estado',
      devoluciones: 'id, ventaId, fecha',
      bitacora: 'id, usuarioId, fecha',
      ajustes: 'id',
    })
    // v2: gastos de la empresa (arriendo, publicidad, compras, etc.).
    this.version(2).stores({
      gastos: 'id, categoria, tipo, fecha',
    })
    // v3-v5: hubo acá un registro propio de bodegas y su stock. Se
    // retiró: ahora las sucursales y su stock son los de POS
    // (pos_sucursales / pos_stock), que esta app administra de forma
    // remota. Las versiones se dejan declaradas para que a quien ya
    // tenga la base creada no le falle la apertura.
    this.version(3).stores({ bodegas: null })
    this.version(4).stores({ stockbodegas: null })
    this.version(5).stores({
      asistencia: 'id, sucursalId, usuarioId, entrada',
    })
  }
}

export const db = new NovaBerryDB()

export const localAdapter: Adapter = {
  nombre: 'local',

  async disponible() {
    return typeof indexedDB !== 'undefined'
  },

  async cargar(): Promise<Snapshot> {
    const [
      usuarios,
      productos,
      lotes,
      movimientos,
      clientes,
      ventas,
      devoluciones,
      bitacora,
      gastos,
      asistencia,
      ajustes,
    ] = await Promise.all([
      db.usuarios.toArray(),
      db.productos.toArray(),
      db.lotes.toArray(),
      db.movimientos.toArray(),
      db.clientes.toArray(),
      db.ventas.toArray(),
      db.devoluciones.toArray(),
      db.bitacora.toArray(),
      db.gastos.toArray(),
      db.asistencia.toArray(),
      db.ajustes.get('ajustes'),
    ])
    return {
      usuarios,
      productos,
      lotes,
      movimientos,
      clientes,
      ventas,
      devoluciones,
      bitacora,
      gastos,
      asistencia,
      ajustes: { ...AJUSTES_POR_DEFECTO, ...ajustes },
    }
  },

  async guardar(tabla, filas) {
    if (!filas.length) return
    // Mismo trato que en Supabase: guardar un producto no toca `stock`
    // ni `reservado`. Acá no hay POS con quien pisarse, pero el store
    // llama igual a los dos adaptadores y tienen que comportarse igual
    // —si no, el mismo formulario dejaría números distintos según el
    // backend.
    if (tabla === 'productos') {
      await db.transaction('rw', db.productos, async () => {
        for (const fila of filas as Producto[]) {
          const guardado = await db.productos.get(fila.id)
          await db.productos.put({
            ...fila,
            stock: guardado?.stock ?? 0,
            reservado: guardado?.reservado ?? 0,
          })
        }
      })
      return
    }
    await (db[tabla] as EntityTable<{ id: string }, 'id'>).bulkPut(filas as { id: string }[])
  },

  async aplicarStock(cambios) {
    const resultado: ResultadoStock = { stock: {}, movimientos: [] }
    if (!cambios.length) return resultado

    await db.transaction('rw', [db.productos, db.movimientos], async () => {
      for (const cambio of cambios) {
        const { stockFinal, ...datos } = cambio

        // Guardia de idempotencia, igual que nb_aplicar_stock: un
        // movimiento ya aplicado no se vuelve a aplicar.
        const ya = await db.movimientos.get(cambio.id)
        const producto = await db.productos.get(cambio.productoId)
        if (!producto) throw new Error(`El producto ${cambio.productoId} ya no existe.`)
        if (ya) {
          resultado.stock[producto.id] = producto.stock
          resultado.movimientos.push({
            id: ya.id,
            productoId: ya.productoId,
            tipo: ya.tipo,
            cantidad: ya.cantidad,
            stockResultante: ya.stockResultante,
          })
          continue
        }

        let cantidad = cambio.cantidad ?? 0
        let tipo = cambio.tipo
        if (typeof stockFinal === 'number') {
          cantidad = stockFinal - producto.stock
          if (tipo === 'ajuste' || tipo === 'merma') tipo = cantidad < 0 ? 'merma' : 'ajuste'
        }

        const stock = producto.stock + cantidad
        if (stock < 0) {
          throw new Error(
            `${producto.nombre}: quedan ${producto.stock} unidades y se intentaban descontar ${Math.abs(cantidad)}.`,
          )
        }

        const movimiento: Movimiento = { ...datos, tipo, cantidad, stockResultante: stock }
        await db.productos.update(producto.id, { stock })
        await db.movimientos.put(movimiento)

        resultado.stock[producto.id] = stock
        resultado.movimientos.push({
          id: movimiento.id,
          productoId: movimiento.productoId,
          tipo,
          cantidad,
          stockResultante: stock,
        })
      }
    })

    return resultado
  },

  async registrarVenta(venta, movimientos, lotes) {
    // Dexie anida transacciones: la de `aplicarStock` se suma a esta en
    // vez de abrir una propia, así que si guardar la venta o los lotes
    // falla, el descuento de stock también se deshace.
    if (await db.ventas.get(venta.id)) {
      const stock: Record<string, number> = {}
      for (const m of movimientos) {
        const p = await db.productos.get(m.productoId)
        if (p) stock[p.id] = p.stock
      }
      return { stock, movimientos: [], folio: venta.folio, repetida: true }
    }
    let resultado!: ResultadoStock
    await db.transaction('rw', [db.productos, db.movimientos, db.ventas, db.lotes], async () => {
      resultado = await localAdapter.aplicarStock(movimientos)
      await db.ventas.put(venta)
      if (lotes.length) await db.lotes.bulkPut(lotes)
    })
    return { ...resultado, folio: venta.folio }
  },

  async eliminar(tabla: Tabla, ids: string[]) {
    if (!ids.length) return
    await (db[tabla] as EntityTable<{ id: string }, 'id'>).bulkDelete(ids)
  },

  async guardarAjustes(ajustes: Ajustes) {
    await db.ajustes.put(ajustes)
  },

  async reemplazarTodo(s: Snapshot) {
    await db.transaction(
      'rw',
      [
        db.usuarios,
        db.productos,
        db.lotes,
        db.movimientos,
        db.clientes,
        db.ventas,
        db.devoluciones,
        db.bitacora,
        db.gastos,
        db.asistencia,
        db.ajustes,
      ],
      async () => {
        await Promise.all([
          db.usuarios.clear(),
          db.productos.clear(),
          db.lotes.clear(),
          db.movimientos.clear(),
          db.clientes.clear(),
          db.ventas.clear(),
          db.devoluciones.clear(),
          db.bitacora.clear(),
          db.gastos.clear(),
          db.asistencia.clear(),
          db.ajustes.clear(),
        ])
        await Promise.all([
          db.usuarios.bulkPut(s.usuarios),
          db.productos.bulkPut(s.productos),
          db.lotes.bulkPut(s.lotes),
          db.movimientos.bulkPut(s.movimientos),
          db.clientes.bulkPut(s.clientes),
          db.ventas.bulkPut(s.ventas),
          db.devoluciones.bulkPut(s.devoluciones),
          db.bitacora.bulkPut(s.bitacora),
          db.gastos.bulkPut(s.gastos),
          db.asistencia.bulkPut(s.asistencia),
          db.ajustes.put(s.ajustes),
        ])
      },
    )
  },
}
